/*
    GESTIÓN DE ALMACENAMIENTO GLOBAL 
        WEB_APP_STORAGE (Local/Session Storage)
        FILE_APP_STORAGE (Almacenamiento de Archivos / Uploads)
*/


import { OBJECT_FREEZE_HELPER } from "../HELPERS/objectFreeze.helper";


const _SCHEMA = {

    WEB_APP_STORAGE: {
        APP_THEME: "APP_THEME",
        APP_LANGUAGE: "APP_LANGUAGE",

        TEMP_EMAIL_VERIFY: "TEMP_EMAIL_VERIFY", // Solo se guarda temporalmente para el proceso de verificación de email en auth (AuthVerifyCodeMain.js), luego se borra al finalizar el proceso (AuthVerifyCodeMain.js) o al volver atrás (AuthLoginMain.js). Se guarda también en location.state para no depender exclusivamente del localStorage/sessionStorage, y así evitar problemas al refrescar la página o usar otro navegador. El valor se setea en AuthLoginMain.js, AuthRegisterMain.js y AuthForgotPasswordMain.js, y se lee principalmente en AuthVerifyCodeMain.js. El valor es el mismo que se muestra en pantalla como email a verificar, y también se envía al backend para validar que el código de verificación corresponde al email correcto.

        TOKEN_USER_AUTH: "TOKEN_USER_AUTH",
        TOKEN_USER_REFRESH: "TOKEN_USER_REFRESH",
        AUTH_VERIFY_EMAIL: "AUTH_VERIFY_EMAIL",
        AUTH_2FA_CHALLENGE: "AUTH_2FA_CHALLENGE",
        AUTH_2FA_EMAIL_MASKED: "AUTH_2FA_EMAIL_MASKED",
        AUTH_2FA_EXPIRES: "AUTH_2FA_EXPIRES",

        CART_USER: "CART_USER",
        FAVORITES_USER: "FAVORITES_USER",
    },

    FILE_APP_STORAGE: {
        /*
            - MAX_SIZE_MB: Tamaño máximo por archivo en MB (null = sin límite)
            - IS_MULTIPLE: true para permitir varios archivos, false para 1 solo
            - MAX_FILES: Cantidad máxima permitida si IS_MULTIPLE es true (null = sin límite de cantidad)
        */
        UPLOAD_SIZE: {
            GIS: { MAX_SIZE_MB: 50, IS_MULTIPLE: true, MAX_FILES: 3 },
            FONT: { MAX_SIZE_MB: 10, IS_MULTIPLE: true, MAX_FILES: 5 },
            CODE: { MAX_SIZE_MB: 5, IS_MULTIPLE: true, MAX_FILES: 20 },
            CAD: { MAX_SIZE_MB: 100, IS_MULTIPLE: true, MAX_FILES: 2 },
            CONFIG: { MAX_SIZE_MB: 1, IS_MULTIPLE: true, MAX_FILES: 5 },
            AUDIO: { MAX_SIZE_MB: 50, IS_MULTIPLE: false, MAX_FILES: 1 },
            IMAGE: { MAX_SIZE_MB: 10, IS_MULTIPLE: true, MAX_FILES: 10 },
            DESIGN: { MAX_SIZE_MB: 100, IS_MULTIPLE: true, MAX_FILES: 3 },
            VIDEO: { MAX_SIZE_MB: 500, IS_MULTIPLE: false, MAX_FILES: 1 },
            SUBTITLE: { MAX_SIZE_MB: 2, IS_MULTIPLE: true, MAX_FILES: 5 },
            ARCHIVE: { MAX_SIZE_MB: 100, IS_MULTIPLE: true, MAX_FILES: 2 },
            DOCUMENT: { MAX_SIZE_MB: 20, IS_MULTIPLE: true, MAX_FILES: 5 },
            MODEL_3D: { MAX_SIZE_MB: 150, IS_MULTIPLE: true, MAX_FILES: 5 },
            CERTIFICATE: { MAX_SIZE_MB: 1, IS_MULTIPLE: true, MAX_FILES: 2 },
            DATABASE: { MAX_SIZE_MB: null, IS_MULTIPLE: false, MAX_FILES: 1 },
            EXECUTABLE: { MAX_SIZE_MB: 50, IS_MULTIPLE: false, MAX_FILES: 1 },
        },

        EXTENSIONS: {
            FONT: ["ttf", "otf", "woff", "woff2", "eot", "ttc", "pfb", "pfm"],
            DATABASE: ["db", "sqlite", "sqlite3", "mdb", "accdb", "mdf", "ndf", "ibd", "frm", "fdb"],
            ARCHIVE: ["zip", "rar", "7z", "tar", "gz", "bz2", "xz", "iso", "tgz", "tbz2", "cab", "wim", "z", "lz"],
            AUDIO: ["mp3", "wav", "ogg", "aac", "m4a", "flac", "wma", "aiff", "opus", "mid", "midi", "amr", "alac"],
            DESIGN: ["psd", "ai", "eps", "indd", "fig", "sketch", "xd", "cdr", "xcf", "afdesign", "afphoto", "afpub"],
            VIDEO: ["mp4", "webm", "mkv", "mov", "avi", "wmv", "flv", "m4v", "3gp", "m2ts", "mts", "ts", "vob", "ogv", "rmvb"],
            EXECUTABLE: ["exe", "msi", "apk", "dmg", "app", "deb", "rpm", "bin", "bat", "cmd", "ps1", "appimage", "pkg", "jar", "vbs", "com"],
            MODEL_3D: ["obj", "fbx", "stl", "gltf", "glb", "blend", "dae", "3ds", "ply", "usdz", "usda", "usdc", "step", "stp", "iges", "igs"],
            IMAGE: ["jpg", "jpeg", "png", "webp", "avif", "gif", "svg", "bmp", "ico", "tiff", "tif", "heic", "heif", "raw", "cr2", "nef", "arw", "dng", "jfif"],
            DOCUMENT: ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv", "rtf", "odt", "ods", "odp", "epub", "md", "markdown", "pages", "numbers", "key", "mobi", "tex"],
            CODE: [
                "js", "ts", "jsx", "tsx", "html", "css", "scss", "sass", "less", "json", "xml", "py",
                "php", "c", "cpp", "h", "hpp", "cs", "java", "sql", "sh", "bash", "yaml", "yml", "go",
                "rs", "rb", "swift", "kt", "kts", "dart", "lua", "r", "scala", "ex", "exs", "vue", "svelte"
            ],
            CAD: ["dwg", "dxf", "skp"],
            GIS: ["geojson", "kml", "kmz", "shp"],
            SUBTITLE: ["srt", "vtt", "ass", "sub", "sbv"],
            CERTIFICATE: ["pem", "crt", "cer", "key", "p12", "pfx", "csr"],
            CONFIG: ["env", "ini", "conf", "toml", "properties", "editorconfig"],
        },

        // El argumento "categories" recibe una categoría ("IMAGE") o un arreglo (["IMAGE", "DOCUMENT"])
        GET_RULES_BY_CATEGORY(categories) {
            const CATEGORY_LIST = Array.isArray(categories) ? categories : [categories];

            return CATEGORY_LIST.reduce((acc, catKey) => {

                const SIZE_RULES = this.UPLOAD_SIZE[catKey];
                const EXT_RULES = this.EXTENSIONS[catKey];

                if (!SIZE_RULES || !EXT_RULES) return acc;

                acc.ALLOWED_EXTENSIONS = Array.from(new Set([...acc.ALLOWED_EXTENSIONS, ...EXT_RULES]));
                acc.IS_MULTIPLE = acc.IS_MULTIPLE || SIZE_RULES.IS_MULTIPLE;

                if (acc.MAX_SIZE_MB !== null) {
                    acc.MAX_SIZE_MB = SIZE_RULES.MAX_SIZE_MB === null ? null : Math.max(acc.MAX_SIZE_MB, SIZE_RULES.MAX_SIZE_MB);
                }

                if (acc.MAX_FILES !== null) {
                    acc.MAX_FILES = SIZE_RULES.MAX_FILES === null ? null : Math.max(acc.MAX_FILES, SIZE_RULES.MAX_FILES);
                }

                return acc;
            }, {
                ALLOWED_EXTENSIONS: [],
                MAX_SIZE_MB: 0, IS_MULTIPLE: false, MAX_FILES: 0
            });
        },

        // ------------------------------- 🔥 VALIDACIÓN DE ARCHIVOS
        /*
            Archivos recibidos desde el input/dropzone
            Objeto de reglas custom o clave(s) de categoría
            Result de validación { IS_VALID, REASON, ERRORS }
        */
        ON_VALIDATE_FILES(filesInput, rulesOrCategories) {
            // Convertir FileList a Array estándar
            const FILES = Array.from(filesInput || []);

            if (FILES.length === 0) {
                return { IS_VALID: false, REASON: "NO_FILES", ERRORS: ["No se proporcionó ningún archivo."] };
            }

            const RULES = typeof rulesOrCategories === "string" || Array.isArray(rulesOrCategories) ? this.GET_RULES_BY_CATEGORY(rulesOrCategories) : rulesOrCategories;

            const ERRORS = [];

            if (!RULES.IS_MULTIPLE && FILES.length > 1) {
                ERRORS.push("Solo se permite adjuntar 1 solo archivo.");
            }

            if (RULES.IS_MULTIPLE && RULES.MAX_FILES !== null && FILES.length > RULES.MAX_FILES) {
                ERRORS.push(`Excediste la cantidad máxima permitida de ${RULES.MAX_FILES} archivos.`);
            }

            FILES.forEach((file) => {
                const FILE_EXT = file.name.split(".").pop().toLowerCase();
                const FILE_SIZE_MB = file.size / (1024 * 1024);

                if (RULES.ALLOWED_EXTENSIONS.length > 0 && !RULES.ALLOWED_EXTENSIONS.includes(FILE_EXT)) {
                    ERRORS.push(`El archivo "${file.name}" no tiene un formato permitido (.${FILE_EXT}).`);
                }

                if (RULES.MAX_SIZE_MB !== null && FILE_SIZE_MB > RULES.MAX_SIZE_MB) {
                    ERRORS.push(`El archivo "${file.name}" (${FILE_SIZE_MB.toFixed(2)} MB) supera el límite máximo de ${RULES.MAX_SIZE_MB} MB.`);
                }
            });

            return {
                IS_VALID: ERRORS.length === 0,
                REASON: ERRORS.length === 0 ? "SUCCESS" : "VALIDATION_FAILED",
                ERRORS,  RULES_APPLIED: RULES
            };
        }
        
        /*
            EJEMPLO USO - Validar directo pasando una categoría:
                const fileInput = event.target.files;
                const validation = FILE_APP_STORAGE.ON_VALIDATE_FILES(fileInput, "IMAGE");
                if (!validation.IS_VALID)  console.error(validation.ERRORS);

            EJEMPLO USO - Validar combinando categorías (ej. Imágenes + Documentos):
                const validation = FILE_APP_STORAGE.ON_VALIDATE_FILES(fileInput, ["IMAGE", "DOCUMENT"]);

            EJEMPLO USO - Extraer las reglas personalizadas para un componente custom
                const customRules = FILE_APP_STORAGE.GET_RULES_BY_CATEGORY("AUDIO");
                const validation = FILE_APP_STORAGE.ON_VALIDATE_FILES(fileInput, customRules);

        */
    }

};


export const APP_CONFIG_STORAGE = OBJECT_FREEZE_HELPER.DEEP_FREEZE(_SCHEMA);

export const APP_STORAGE = OBJECT_FREEZE_HELPER.DEEP_FREEZE({
    ..._SCHEMA.WEB_APP_STORAGE,
    ..._SCHEMA.FILE_APP_STORAGE,
});
